import { PAGE_SIZE } from "./constants";
import { uploadEntityAttachments, fetchEntityAttachmentsByIds, toNamedAttachment, deleteAttachment } from "@/lib/entity-attachments";
import { PurchaseOrder, PurchaseOrderAttachment, NewPurchaseOrderInput } from "./types";
import { fetchEntity } from "@/lib/entity-api";
import { useEntityList } from "@/hooks/useEntityList";
import { getUserId } from "@/lib/auth-session";
import { getApiUrl } from '@/lib/api-config';
import { useProject } from "@/components/ProjectSelector";
import { useUser } from "@/contexts/UserContext";
import { formatUserDisplayName } from "@/lib/user-display";

function mapRowToPurchaseOrder(row: any): PurchaseOrder {
  return {
    id: row.id,
    poNumber: row.po_number,
    title: row.title ?? "",
    description: row.description ?? undefined,
    projectId: row.project_id,
    vendorId: row.vendor_id ?? undefined,
    changeOrderId: row.change_order_id ?? undefined,
    materialListId: row.material_list_id ?? undefined,
    amount: Number(row.amount ?? 0),
    status: row.status,
    sentAt: row.sent_at ?? undefined,
    receivedAt: row.complete_by ? String(row.complete_by).split('T')[0] : undefined,
    attachments: [],
    createdAt: row.created_at,
    author: row.created_by_name || "Unknown User",
    commentsCount: typeof row.comment_count === "number" ? row.comment_count : 0,
    milestones: Array.isArray(row.milestones) ? row.milestones : undefined,
    costCodeId: row.cost_code_id ?? undefined,
    taxPct: Number(row.tax_pct ?? 0),
    otherAmount: Number(row.other_amount ?? 0),
  };
}

async function fetchAttachmentsForIds(ids: string[]): Promise<Map<string, PurchaseOrderAttachment[]>> {
  const grouped = await fetchEntityAttachmentsByIds('purchase_order', ids);
  return new Map(Array.from(grouped, ([id, list]) => [id, list.map((att) => ({
    ...toNamedAttachment(att),
    // The list shows the uploader's name, not their id.
    uploadedBy: att.created_by_user ? formatUserDisplayName(att.created_by_user, att.created_by) : att.created_by ?? undefined,
  }))]));
}

const toCompletionTimestamp = (date: string) => {
  // Store as a pure date at UTC midnight without shifting the calendar day
  const d = new Date(date + 'T00:00:00');
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate())).toISOString();
};

/** Creates a purchase order through the API (the backend derives company, author and PO number). */
export async function createPurchaseOrder(input: NewPurchaseOrderInput, author: string): Promise<PurchaseOrder | undefined> {
  const payload: any = {
    project_id: input.projectId,
    vendor_id: input.vendorId ?? null,
    title: input.title || null,
    description: input.description || null,
    amount: input.amount,
    status: input.status,
    complete_by: input.completionDate ? toCompletionTimestamp(input.completionDate) : null,
    milestones: Array.isArray(input.milestones) && input.milestones.length > 0 ? input.milestones : null,
    cost_code_id: input.costCodeId || null,
    ...(input.warrantyId ? { warranty_id: input.warrantyId } : {}),
    ...(input.changeOrderId ? { change_order_id: input.changeOrderId } : {}),
    ...(input.selectionId ? { selection_id: input.selectionId } : {}),
    ...(input.materialListId ? { material_list_id: input.materialListId } : {}),
    ...(input.lines !== undefined ? { lines: input.lines } : {}),
    ...(input.taxPct !== undefined ? { tax_pct: input.taxPct } : {}),
    ...(input.otherAmount !== undefined ? { other_amount: input.otherAmount } : {}),
  };

  const response = await fetch(getApiUrl('api/entities/purchase_order'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const result = await response.json().catch(() => ({}));
  // a failure is never a silent "nothing happened": the form shows the server's own message and stays open
  if (!response.ok || !result.success || !result.data) {
    console.error('Failed to create purchase order', result);
    throw new Error(result?.error || result?.message || 'Failed to create the purchase order');
  }

  const created = mapRowToPurchaseOrder(result.data);
  created.author = author;
  // A new record has no comments; its files are read once (they may have been uploaded already).
  const attachmentsById = await fetchAttachmentsForIds([created.id]);
  created.attachments = attachmentsById.get(created.id) ?? input.attachments ?? [];
  return created;
}

/** One purchase order by id, as the list shows it (a link from another document opens it); undefined when it no longer exists or the caller cannot read it. */
export async function fetchPurchaseOrderById(id: string): Promise<PurchaseOrder | undefined> {
  try {
    const [row, attachmentsById] = await Promise.all([
      fetchEntity<any>('purchase_order', id, { includeCounts: true, includeCreator: true }),
      fetchAttachmentsForIds([id]),
    ]);
    if (!row) return undefined;
    const order = mapRowToPurchaseOrder(row);
    order.attachments = attachmentsById.get(id) ?? [];
    return order;
  } catch (error) {
    console.error('Could not open the purchase order:', error);
    return undefined;
  }
}

/** Updates a purchase order and returns it fresh (counter, creator and files computed by the server). */
export async function updatePurchaseOrderRecord(id: string, updates: Partial<NewPurchaseOrderInput>, currentProjectId?: string): Promise<PurchaseOrder | undefined> {
  const payload: any = {};
  // Don't include po_number - it's auto-generated and shouldn't be updated
  // Always include project_id to satisfy potential WITH CHECK policies
  payload.project_id = updates.projectId ?? currentProjectId;
  if (updates.vendorId !== undefined) payload.vendor_id = updates.vendorId;
  if (updates.title !== undefined) payload.title = updates.title ?? null;
  if (updates.description !== undefined) payload.description = updates.description ?? null;
  if (updates.amount !== undefined) payload.amount = updates.amount;
  if (updates.status !== undefined) payload.status = updates.status;
  if (updates.completionDate !== undefined) payload.complete_by = updates.completionDate ? toCompletionTimestamp(updates.completionDate) : null;
  if (updates.costCodeId !== undefined) payload.cost_code_id = updates.costCodeId || null;
  if (updates.milestones !== undefined) {
    payload.milestones = Array.isArray(updates.milestones) && updates.milestones.length > 0 ? updates.milestones : null;
  }
  if (updates.lines !== undefined) payload.lines = updates.lines;
  if (updates.taxPct !== undefined) payload.tax_pct = updates.taxPct;
  if (updates.otherAmount !== undefined) payload.other_amount = updates.otherAmount;

  const response = await fetch(getApiUrl(`api/entities/purchase_order/${id}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const result = await response.json().catch(() => ({}));
  if (!response.ok || !result.success || !result.data) {
    console.error('Failed to update purchase order:', result);
    throw new Error(result?.error || result?.message || 'Failed to update the purchase order');
  }

  const [fresh, attachmentsById] = await Promise.all([
    fetchEntity<any>('purchase_order', id, { includeCounts: true, includeCreator: true }),
    fetchAttachmentsForIds([id]),
  ]);
  const updated = mapRowToPurchaseOrder(fresh);
  updated.attachments = attachmentsById.get(id) ?? [];
  return updated;
}

export async function uploadPurchaseOrderAttachments(purchaseOrderId: string, files: File[], companyId: string): Promise<PurchaseOrderAttachment[]> {
  if (!purchaseOrderId || files.length === 0) return [];
  const sessionUserId = getUserId() ?? undefined;
  if (!sessionUserId) return [];
  return (await uploadEntityAttachments('purchase_order', purchaseOrderId, files, companyId, sessionUserId)).map(toNamedAttachment);
}

export async function deletePurchaseOrderAttachment(attachmentId: string): Promise<boolean> {
  return deleteAttachment(attachmentId, getUserId() ?? undefined);
}

/**
 * The Purchase Orders list: the generic server-driven list (hooks/useEntityList.ts) with this module's
 * row mapping and per-page files. Search / sort / project / paging all happen on the server over the whole
 * dataset; `exportRows` exports every record of the current query.
 */
export function usePurchaseOrders() {
  const { selectedProject } = useProject();
  const { companyId } = useUser();
  const list = useEntityList<any, PurchaseOrder>({
    entityType: 'purchase_order',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'append',
    pageSize: PAGE_SIZE,
    includeCounts: true,
    includeCreator: true,
    mapRow: mapRowToPurchaseOrder,
    enrichPage: async (rows, items) => {
      const attachmentsById = await fetchAttachmentsForIds(rows.map((r) => r.id));
      return items.map((item) => ({ ...item, attachments: attachmentsById.get(item.id) ?? [] }));
    },
  });
  const { setItems, reload } = list;

  return {
    ...list,
    /** A new record: where it belongs depends on the server's sort / search, so the first page is read again. */
    async addPurchaseOrder(input: NewPurchaseOrderInput, author: string): Promise<PurchaseOrder | undefined> {
      const created = await createPurchaseOrder(input, author);
      if (created) reload();
      return created;
    },
    async updatePurchaseOrder(id: string, updates: Partial<NewPurchaseOrderInput>): Promise<PurchaseOrder | undefined> {
      const current = list.items.find((c) => c.id === id);
      const updated = await updatePurchaseOrderRecord(id, updates, current?.projectId);
      if (updated) setItems((prev) => prev.map((c) => (c.id === id ? updated : c)));
      return updated;
    },
  };
}
